import React, { useState } from 'react';
import axios from 'axios';
import { getCSRFToken } from '../utils/csrf';
import './ScheduleImportModal.css';

const ScheduleImportModal = ({ isOpen, onClose, gradeLevel, blockSchedule, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState(null);
    const [errors, setErrors] = useState([]);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setErrors([]);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            setFile(droppedFile);
            setErrors([]);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleFileUpload = async () => {
        if (!file) {
            setErrors(['Please select a file to upload']);
            return;
        }

        setIsProcessing(true);
        setErrors([]);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('grade_level', gradeLevel);
        formData.append('block_schedule', blockSchedule);

        try {
            const response = await axios.post(
                '/schedules/api/import-section-periods/',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'X-CSRFToken': getCSRFToken()
                    }
                }
            );

            setResults(response.data);
            setStep(3);
            if (onSuccess) {
                onSuccess(response.data);
            }
        } catch (error) {
            console.error('Import error:', error);
            if (error.response && error.response.data) {
                if (error.response.data.errors) {
                    setErrors(error.response.data.errors);
                } else {
                    setErrors([error.response.data.error || 'An error occurred during import']);
                }
            } else {
                setErrors(['An unexpected error occurred']);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    const resetModal = () => {
        setStep(1);
        setFile(null);
        setResults(null);
        setErrors([]);
        setIsProcessing(false);
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="schedule-import-modal-overlay">
            <div className="schedule-import-modal">
                <div className="schedule-import-modal-header">
                    <h2>📅 Import Section Periods</h2>
                    <button className="close-button" onClick={handleClose}>×</button>
                </div>

                <div className="schedule-import-modal-content">
                    {step === 1 && (
                        <div className="import-step">
                            <h3>Step 1: Prepare Your Spreadsheet</h3>
                            <div className="format-instructions">
                                <h4>📋 Required Format:</h4>
                                <div className="format-example">
                                    <h4>📋 Option 1: Time Ranges Only (Most Common)</h4>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Time Range</th>
                                                <th>Days (Optional)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>8:00-8:30</td>
                                                <td>Monday,Tuesday,Wednesday,Thursday</td>
                                            </tr>
                                            <tr>
                                                <td>8:30-9:10</td>
                                                <td>Monday,Tuesday,Wednesday,Thursday</td>
                                            </tr>
                                            <tr>
                                                <td>9:10-10:00</td>
                                                <td>Monday,Tuesday,Wednesday,Thursday</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    
                                    <h4>📋 Option 2: With Period Names</h4>
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Period Name</th>
                                                <th>Time Range</th>
                                                <th>Days (Optional)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Period 1</td>
                                                <td>8:00-8:30</td>
                                                <td>Monday,Tuesday,Wednesday,Thursday</td>
                                            </tr>
                                            <tr>
                                                <td>Period 2</td>
                                                <td>8:30-9:10</td>
                                                <td>Monday,Tuesday,Wednesday,Thursday</td>
                                            </tr>
                                            <tr>
                                                <td>Period 3</td>
                                                <td>9:10-10:00</td>
                                                <td>Monday,Tuesday,Wednesday,Thursday</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div className="format-tips">
                                    <h4>💡 Tips:</h4>
                                    <ul>
                                        <li><strong>Simple Format:</strong> Just list time ranges like "8:00-8:30" (one per row)</li>
                                        <li><strong>Time Format:</strong> Use "8:00-8:30" format (24-hour)</li>
                                        <li><strong>Smart Detection:</strong> 8:00-8:30 and 3:00-3:15 automatically set as Homeroom periods</li>
                                        <li><strong>Auto-Categorization:</strong> All other times automatically set as Regular Class Periods</li>
                                        <li><strong>Days:</strong> If not specified, defaults to Monday-Thursday</li>
                                        <li><strong>File Types:</strong> Excel (.xlsx, .xls) or CSV files</li>
                                        <li><strong>Grade Level:</strong> All periods will be created for Grade {gradeLevel}</li>
                                        <li><strong>Auto-Naming:</strong> Periods will be automatically named "Period 1", "Period 2", etc.</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="step-actions">
                                <button className="next-button" onClick={() => setStep(2)}>
                                    Next → Upload File
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="import-step">
                            <h3>Step 2: Upload Your File</h3>
                            <div 
                                className="drop-zone"
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                            >
                                <div className="drop-zone-content">
                                    {file ? (
                                        <div className="selected-file">
                                            <div className="file-icon">📄</div>
                                            <div className="file-info">
                                                <div className="file-name">{file.name}</div>
                                                <div className="file-size">
                                                    {(file.size / 1024).toFixed(1)} KB
                                                </div>
                                            </div>
                                            <button 
                                                className="remove-file"
                                                onClick={() => setFile(null)}
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="drop-zone-prompt">
                                            <div className="drop-icon">📁</div>
                                            <p>Drag and drop your spreadsheet here, or click to browse</p>
                                            <input
                                                type="file"
                                                accept=".xlsx,.xls,.csv"
                                                onChange={handleFileSelect}
                                                className="file-input"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {errors.length > 0 && (
                                <div className="error-messages">
                                    {errors.map((error, index) => (
                                        <div key={index} className="error-message">
                                            ⚠️ {error}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="step-actions">
                                <button className="back-button" onClick={() => setStep(1)}>
                                    ← Back
                                </button>
                                <button 
                                    className="import-button"
                                    onClick={handleFileUpload}
                                    disabled={!file || isProcessing}
                                >
                                    {isProcessing ? (
                                        <>
                                            <div className="spinner"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        '📅 Import Periods'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="import-step">
                            <h3>✅ Import Complete!</h3>
                            {results && (
                                <div className="import-results">
                                    <div className="results-summary">
                                        <div className="success-count">
                                            🎉 {results.periods_created} periods created successfully
                                        </div>
                                        {results.errors.length > 0 && (
                                            <div className="error-count">
                                                ⚠️ {results.errors.length} issues found
                                            </div>
                                        )}
                                    </div>

                                    {results.created_periods.length > 0 && (
                                        <div className="created-periods">
                                            <h4>📅 Created Periods:</h4>
                                            <div className="periods-list">
                                                {results.created_periods.map((period, index) => (
                                                    <div key={index} className="period-item">
                                                        <strong>{period.name}</strong> - {period.start_time} to {period.end_time}
                                                        <div className="period-details">
                                                            Grade {period.grade_level} • {period.days.join(', ')} • 
                                                            <span className={`period-type ${period.period_type}`}>
                                                                {period.homeroom ? '🏠 Homeroom' : '📚 Regular Class'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {results.errors.length > 0 && (
                                        <div className="import-errors">
                                            <h4>⚠️ Issues Found:</h4>
                                            <div className="error-list">
                                                {results.errors.map((error, index) => (
                                                    <div key={index} className="error-item">
                                                        {error}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="step-actions">
                                <button className="finish-button" onClick={handleClose}>
                                    🎉 Done
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScheduleImportModal;
